import { FormProvider, type Path, useForm } from "react-hook-form";

import FormSelectInput, { type SelectOptionType } from "./FormSelectInput";

interface StandaloneSelectProps<T> {
  name: string;
  options: { label: string; value: T }[];
  placeholder?: string;
  label?: string;
  highlightBorder?: boolean;
  className?: string;
  selectClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  defaultValue?: T;
  onChange?: (value: T) => void;
  onSubmit?: (data: { value: T }) => void;
  hideLabel?: boolean;
  icon?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
}

export function StandaloneSelect<TSchema>({
  name,
  options,
  placeholder,
  label,
  highlightBorder,
  className,
  selectClassName,
  contentClassName,
  iconClassName,
  defaultValue = "" as TSchema,
  onChange,
  onSubmit,
  hideLabel,
  icon,
  disabled,
  readOnly,
}: StandaloneSelectProps<TSchema>) {
  const methods = useForm<{ value: TSchema }>({
    defaultValues: { [name]: defaultValue } as { value: TSchema },
  });

  const handleSubmit = onSubmit ? methods.handleSubmit(onSubmit) : () => {};

  if (onChange) {
    methods.watch((data) => {
      if (data[name as keyof typeof data]) {
        onChange(data[name as keyof typeof data] as TSchema);
      }
    });
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className={className}>
        <FormSelectInput<{ value: TSchema }>
          contentClassName={contentClassName}
          className={selectClassName}
          iconClassName={iconClassName}
          name={name as Path<{ value: TSchema }>}
          hideLabel={hideLabel}
          options={options as unknown as SelectOptionType[]}
          placeholder={placeholder}
          label={label}
          highlightBorder={highlightBorder}
          icon={icon}
          disabled={disabled}
          readOnly={readOnly}
        />
      </form>
    </FormProvider>
  );
}
